import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BedDouble, Shuffle, SlidersHorizontal, Lock, LockOpen, Plus, X, AlertTriangle, LogOut, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BottomSheet, Field } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

// Gruppo di una persona: coppia bucket+sesso. Chi non ha sesso valido → null.
const grpKey = (o) => (o?.sesso === "M" || o?.sesso === "F") ? `${o.bucket}-${o.sesso}` : null;
const bucketLabel = (b) => (b === "resp" ? "Responsabili" : "Animatori");
const sessoLabel = (s) => (s === "M" ? "Maschile" : s === "F" ? "Femminile" : "—");

function gruppoStanza(occ) {
  if (!occ || !occ.length) return null;
  const o = occ[0];
  return { key: grpKey(o), testo: `${bucketLabel(o.bucket)} · ${o.sesso}`, bucket: o.bucket };
}

// Una stanza è valida se non supera la capienza e contiene un solo gruppo (senza persone senza sesso)
function stanzaValida(occ, cap) {
  if (occ.length > cap) return false;
  const gs = new Set(occ.map(grpKey));
  if (gs.has(null)) return false;
  return gs.size <= 1;
}

function PersonaChip({ p, selezionato, onSelect, onLock, dimmed }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-1 text-xs font-bold border transition-all ${
      selezionato ? "bg-amber-500 text-white border-amber-500 ring-2 ring-amber-300"
      : dimmed ? "bg-slate-50 border-slate-200 text-slate-300"
      : "bg-white border-slate-200 text-slate-700 hover:border-amber-400"}`}
      data-testid={`persona-${p.id}`}>
      <button type="button" onClick={onSelect} className="truncate max-w-[130px]">{p.nome} {p.cognome}</button>
      {onLock && (
        <button type="button" onClick={onLock} className={selezionato ? "text-white" : p.bloccato ? "text-amber-600" : "text-slate-300 hover:text-slate-500"} aria-label={p.bloccato ? "Sblocca" : "Blocca nella stanza"} data-testid={`lock-${p.id}`}>
          {p.bloccato ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
        </button>
      )}
    </span>
  );
}

export default function CamposcuolaStanze() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);       // pid selezionato per spostamento/scambio
  const [generando, setGenerando] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [rimuoviOpen, setRimuoviOpen] = useState(false);
  const [tipi, setTipi] = useState([{ capienza: 2, quantita: 0 }]);

  const carica = useCallback(() => {
    api.get("/camposcuola/stanze").then((r) => setData(r.data)).catch((err) => toast.error(errMsg(err)));
  }, []);
  useEffect(() => { carica(); }, [carica]);
  useRefreshOnPull(carica);

  if (!data) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const selPersona = (() => {
    if (!sel) return null;
    for (const s of data.stanze) { const p = s.occupanti.find((o) => o.id === sel); if (p) return { ...p, doveRoom: s.id }; }
    const p = data.non_assegnati.find((o) => o.id === sel); if (p) return { ...p, doveRoom: null };
    return null;
  })();

  // Persistenza: aggiornamento ottimistico + PUT, poi si riallinea alla risposta
  const persist = async (stanze, nonAssegnati, bloccati) => {
    setData((d) => ({ ...d, stanze, non_assegnati: nonAssegnati, bloccati }));
    try {
      const { data: res } = await api.put("/camposcuola/stanze/salva", {
        stanze: stanze.map((s) => ({ id: s.id, occupanti: s.occupanti.map((o) => o.id) })),
        bloccati,
      });
      setData(res);
    } catch (err) { toast.error(errMsg(err)); carica(); }
  };

  const clonaStanze = () => data.stanze.map((s) => ({ ...s, occupanti: [...s.occupanti] }));

  const muovi = (pid, target) => {
    const stanze = clonaStanze();
    let non = [...data.non_assegnati];
    let persona = null;
    for (const s of stanze) { const i = s.occupanti.findIndex((o) => o.id === pid); if (i >= 0) { persona = s.occupanti[i]; s.occupanti.splice(i, 1); break; } }
    if (!persona) { const i = non.findIndex((o) => o.id === pid); if (i >= 0) { persona = non[i]; non.splice(i, 1); } }
    if (!persona) return;
    if (target === "non") {
      non.push(persona);
    } else {
      const room = stanze.find((s) => s.id === target);
      if (!room) return;
      if (!stanzaValida([...room.occupanti, persona], room.capienza)) { toast.error("Spostamento non valido: capienza piena o gruppo diverso"); return; }
      room.occupanti.push(persona);
    }
    setSel(null);
    persist(stanze, non, data.bloccati);
  };

  const scambia = (a, b) => {
    const stanze = clonaStanze();
    let non = [...data.non_assegnati];
    const trova = (pid) => {
      for (const s of stanze) { const i = s.occupanti.findIndex((o) => o.id === pid); if (i >= 0) return { arr: s.occupanti, i, room: s }; }
      const i = non.findIndex((o) => o.id === pid); if (i >= 0) return { arr: non, i, room: null };
      return null;
    };
    const la = trova(a), lb = trova(b);
    if (!la || !lb) return;
    const pa = la.arr[la.i], pb = lb.arr[lb.i];
    la.arr[la.i] = pb; lb.arr[lb.i] = pa;
    for (const r of [la.room, lb.room]) if (r && !stanzaValida(r.occupanti, r.capienza)) { toast.error("Scambio non valido: gruppi incompatibili"); return; }
    setSel(null);
    persist(stanze, non, data.bloccati);
  };

  // Tap su una persona: seleziona, deseleziona o scambia con quella già selezionata
  const tapPersona = (pid) => {
    if (!sel) { setSel(pid); return; }
    if (sel === pid) { setSel(null); return; }
    scambia(sel, pid);
  };

  const toggleLock = (pid) => {
    const bloccati = data.bloccati.includes(pid) ? data.bloccati.filter((x) => x !== pid) : [...data.bloccati, pid];
    persist(data.stanze, data.non_assegnati, bloccati);
  };

  const rimuoviTutti = (ancheBloccati) => {
    const stanze = data.stanze.map((s) => ({
      ...s,
      occupanti: ancheBloccati ? [] : s.occupanti.filter((o) => data.bloccati.includes(o.id)),
    }));
    const bloccati = ancheBloccati ? [] : data.bloccati;
    const restano = new Set(stanze.flatMap((s) => s.occupanti.map((o) => o.id)));
    const tutte = [...data.stanze.flatMap((s) => s.occupanti), ...data.non_assegnati];
    const non = tutte.filter((p) => !restano.has(p.id));
    setSel(null);
    setRimuoviOpen(false);
    persist(stanze, non, bloccati);
  };

  const genera = async () => {
    setGenerando(true);
    try {
      const { data: res } = await api.post("/camposcuola/stanze/genera");
      setData(res); setSel(null);
      toast.success("Stanze generate");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setGenerando(false); }
  };

  const apriConfig = () => {
    // Prefill dai tipi attuali (raggruppa per capienza)
    const conteggi = {};
    data.stanze.forEach((s) => { conteggi[s.capienza] = (conteggi[s.capienza] || 0) + 1; });
    const righe = Object.entries(conteggi).map(([capienza, quantita]) => ({ capienza: Number(capienza), quantita }));
    setTipi(righe.length ? righe : [{ capienza: 2, quantita: 2 }]);
    setConfigOpen(true);
  };

  const salvaConfig = async () => {
    const validi = tipi
      .map((t) => ({ capienza: Number(t.capienza), quantita: Number(t.quantita) }))
      .filter((t) => t.capienza > 0 && t.quantita > 0);
    if (!validi.length) return toast.error("Aggiungi almeno una stanza (capienza e quantità)");
    try {
      const { data: res } = await api.put("/camposcuola/stanze/config", { tipi: validi });
      setData(res); setSel(null); setConfigOpen(false);
      toast.success("Stanze configurate");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const totPosti = data.stanze.reduce((a, s) => a + s.capienza, 0);
  const totOccupati = data.stanze.reduce((a, s) => a + s.occupanti.length, 0);
  const totPersone = totOccupati + data.non_assegnati.length;
  const nSenzaSesso = (data.senza_sesso || []).length;
  const nBloccati = (data.bloccati || []).length;

  // Bersaglio valido per lo spostamento del selezionato in una certa stanza
  const puoAndare = (room) => {
    if (!selPersona || selPersona.doveRoom === room.id) return false;
    return stanzaValida([...room.occupanti, selPersona], room.capienza);
  };

  return (
    <div data-testid="camposcuola-stanze-page" className="max-w-5xl">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <Link to="/camposcuola" className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 mb-1" data-testid="stanze-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Camposcuola
          </Link>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <BedDouble className="h-6 w-6 text-amber-500" /> Stanze
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost" onClick={apriConfig} data-testid="stanze-config-btn">
            <SlidersHorizontal className="h-4 w-4" /> Configura
          </button>
          <button className="btn-ghost text-red-500" onClick={() => setRimuoviOpen(true)} disabled={!totOccupati} data-testid="stanze-rimuovi-tutti-btn">
            <Trash2 className="h-4 w-4" /> Rimuovi tutti
          </button>
          <button className="btn-primary" onClick={genera} disabled={generando || !data.stanze.length} data-testid="stanze-genera-btn">
            <Shuffle className="h-4 w-4" /> {generando ? "Genero..." : "Genera casuale"}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 font-semibold mb-3">
        {data.stanze.length} stanze · {totPosti} posti · {totPersone} partecipanti
        {nSenzaSesso > 0 && <span className="text-amber-600"> · {nSenzaSesso} senza sesso</span>}
      </p>

      {sel && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 mb-3 text-xs font-bold text-amber-800 flex items-center justify-between gap-2" data-testid="stanze-selezione-hint">
          <span>Selezionato: {selPersona?.nome} {selPersona?.cognome}. Tocca una persona per scambiare, o «Sposta qui» in una stanza.</span>
          <button className="shrink-0 underline" onClick={() => setSel(null)}>Annulla</button>
        </div>
      )}

      {nSenzaSesso > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 mb-4 flex items-start gap-2" data-testid="stanze-senza-sesso">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs font-semibold text-amber-800">
            {nSenzaSesso} partecipant{nSenzaSesso === 1 ? "e" : "i"} senza sesso indicato: non {nSenzaSesso === 1 ? "viene" : "vengono"} assegnat{nSenzaSesso === 1 ? "o" : "i"} finché non imposti il sesso nel loro profilo (o anagrafica).
          </p>
        </div>
      )}

      {data.stanze.length === 0 ? (
        <div className="card-app p-6 text-center">
          <p className="font-bold text-slate-700 mb-1">Nessuna stanza configurata</p>
          <p className="text-sm text-slate-500 font-semibold mb-4">Definisci quante stanze e di che capienza, poi genera l'assegnazione.</p>
          <button className="btn-primary mx-auto" onClick={apriConfig}><SlidersHorizontal className="h-4 w-4" /> Configura le stanze</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {data.stanze.map((s, idx) => {
            const g = gruppoStanza(s.occupanti);
            const liberi = s.capienza - s.occupanti.length;
            const target = puoAndare(s);
            return (
              <div key={s.id} className={`card-app p-3.5 ${target ? "ring-2 ring-amber-400" : ""}`} data-testid={`stanza-${s.id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="font-display font-extrabold text-slate-900 text-sm">Stanza {idx + 1}</p>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{s.occupanti.length}/{s.capienza} posti</span>
                </div>
                {g ? (
                  <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 ${g.bucket === "resp" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{g.testo}</span>
                ) : (
                  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 bg-slate-100 text-slate-400">Vuota</span>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {s.occupanti.map((p) => (
                    <PersonaChip key={p.id} p={p} selezionato={sel === p.id} onSelect={() => tapPersona(p.id)} onLock={() => toggleLock(p.id)} />
                  ))}
                  {liberi > 0 && Array.from({ length: liberi }).map((_, i) => (
                    <span key={i} className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold text-slate-300 border border-dashed border-slate-200">libero</span>
                  ))}
                </div>
                {target && (
                  <button type="button" onClick={() => muovi(sel, s.id)} className="btn-secondary !py-1.5 text-xs w-full mt-2.5" data-testid={`sposta-qui-${s.id}`}>
                    Sposta qui {selPersona?.nome}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Non assegnati */}
      <div className="card-app p-4" data-testid="stanze-non-assegnati">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="font-display font-extrabold text-slate-900 text-sm">Non assegnati ({data.non_assegnati.length})</p>
          {sel && selPersona?.doveRoom && (
            <button type="button" onClick={() => muovi(sel, "non")} className="btn-ghost !py-1.5 text-xs" data-testid="sposta-non-assegnati">
              <LogOut className="h-3.5 w-3.5" /> Togli dalla stanza
            </button>
          )}
        </div>
        {data.non_assegnati.length === 0 ? (
          <p className="text-xs text-slate-400 font-semibold">Tutti assegnati.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.non_assegnati.map((p) => {
              const senzaSesso = grpKey(p) === null;
              return (
                <PersonaChip key={p.id} p={p} selezionato={sel === p.id} dimmed={senzaSesso}
                  onSelect={() => senzaSesso ? toast.error(`${p.nome}: manca il sesso, non assegnabile`) : tapPersona(p.id)} />
              );
            })}
          </div>
        )}
      </div>

      {/* Sheet configurazione stanze */}
      <BottomSheet open={configOpen} onOpenChange={setConfigOpen} title="Configura le stanze"
        description="Indica quante stanze per ogni capienza. Rigenerare la configurazione azzera l'assegnazione attuale." testId="stanze-config-sheet">
        <div className="space-y-2.5">
          {tipi.map((t, i) => (
            <div key={i} className="flex items-end gap-2">
              <Field label="Capienza"><input type="number" min="1" className="input-app" value={t.capienza}
                onChange={(e) => setTipi(tipi.map((x, j) => j === i ? { ...x, capienza: e.target.value } : x))} data-testid={`config-capienza-${i}`} /></Field>
              <Field label="Quante"><input type="number" min="0" className="input-app" value={t.quantita}
                onChange={(e) => setTipi(tipi.map((x, j) => j === i ? { ...x, quantita: e.target.value } : x))} data-testid={`config-quantita-${i}`} /></Field>
              <button type="button" className="btn-ghost !p-2 mb-1 text-red-500" onClick={() => setTipi(tipi.filter((_, j) => j !== i))} aria-label="Rimuovi" data-testid={`config-remove-${i}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-ghost !py-2 text-xs mt-2" onClick={() => setTipi([...tipi, { capienza: 2, quantita: 1 }])} data-testid="config-add-tipo">
          <Plus className="h-4 w-4" /> Aggiungi capienza
        </button>
        <p className="text-xs text-slate-400 font-semibold mt-3">
          Totale: {tipi.reduce((a, t) => a + (Number(t.capienza) * Number(t.quantita) || 0), 0)} posti in {tipi.reduce((a, t) => a + (Number(t.quantita) || 0), 0)} stanze
        </p>
        <button className="btn-primary w-full mt-3" onClick={salvaConfig} data-testid="config-salva-btn">Salva configurazione</button>
      </BottomSheet>

      {/* Sheet conferma "Rimuovi tutti": chiede se includere anche i bloccati */}
      <BottomSheet open={rimuoviOpen} onOpenChange={setRimuoviOpen} title="Rimuovere tutti dalle stanze?"
        description={nBloccati > 0
          ? `Ci sono ${nBloccati} person${nBloccati === 1 ? "a bloccata" : "e bloccate"} con il lucchetto. Vuoi rimuovere anche quelle?`
          : "Tutti i partecipanti torneranno tra i «non assegnati»."} testId="stanze-rimuovi-sheet">
        <div className="space-y-2">
          {nBloccati > 0 ? (
            <>
              <button className="btn-secondary w-full" onClick={() => rimuoviTutti(false)} data-testid="rimuovi-solo-non-bloccati">
                Solo i non bloccati ({totOccupati - nBloccati})
              </button>
              <button className="btn-danger w-full" onClick={() => rimuoviTutti(true)} data-testid="rimuovi-anche-bloccati">
                <Trash2 className="h-4 w-4" /> Anche i bloccati (tutti, {totOccupati})
              </button>
            </>
          ) : (
            <button className="btn-danger w-full" onClick={() => rimuoviTutti(true)} data-testid="rimuovi-tutti-conferma">
              <Trash2 className="h-4 w-4" /> Rimuovi tutti ({totOccupati})
            </button>
          )}
          <button className="btn-ghost w-full" onClick={() => setRimuoviOpen(false)} data-testid="rimuovi-annulla">Annulla</button>
        </div>
      </BottomSheet>
    </div>
  );
}
